import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from "@fxc/ui-kit";
import { GRAPHQL_MAX_INT } from "graphql";
import { useId, useMemo } from "react";
import {
  ControllerRenderProps,
  UseFormReturn,
  useController,
} from "react-hook-form";

import SelectElement from "@/components/SelectElement";
import { Scope } from "@/components/ProtectedRoute";
import { getClientFormInfo } from "@/entities/client/lib/getClientFormInfo";
import { InputBaseURL } from "@/entities/client/ui/InputBaseURL";
import { SwitchElement } from "react-hook-form-mui";
import TextFieldElement from "@/components/TextFieldElement";
import {
  ClientQuery,
  DefaultInfoQuery,
  Feature,
  ProposalNavigationPositionType,
  Role,
} from "../../../../generated/apollo";

// analytics, data sources, templates, proposals, lookups
export const ScopeOptions = [
  {
    id: Scope.Proposals,
    label: "Proposals",
    availableFor: [Role.FxcAdmin, Role.Admin, Role.User, Role.Viewer],
  },
  {
    id: Scope.Insights,
    label: "Insights",
    availableFor: [Role.FxcAdmin, Role.Admin, Role.User, Role.Viewer],
  },
  {
    id: Scope.Analytics,
    label: "Analytics",
    availableFor: [Role.FxcAdmin, Role.Admin, Role.User, Role.Viewer],
  },
  {
    id: Scope.Templates,
    label: "Templates",
    availableFor: [Role.FxcAdmin, Role.Admin],
  },
  {
    id: Scope.DataSources,
    label: "Data Sources",
    availableFor: [Role.FxcAdmin, Role.Admin],
  },
  {
    id: Scope.Lookups,
    label: "Lookups",
    availableFor: [Role.FxcAdmin, Role.Admin],
  },
  {
    id: Scope.Clients,
    label: "Clients",
    availableFor: [Role.FxcAdmin],
  },
  {
    id: Scope.Client,
    label: "Client",
    availableFor: [Role.Admin, Role.User, Role.Viewer],
  },
];

export const CLIENT_EDITABLE_PROPS = [
  "usersLimit",
  "applicationBaseUrl",
  "applicationBrandColor",
  "applicationLogoUrl",
  "applicationTitle",
  "keycloakGroupName",
  "proposalNavigationPosition",
  "clientPermissions",
  "entityNamePlural",
  "entityNameSingular",
  "insightPlaceholderSingular",
  "insightPlaceholderPlural",
  "clientFeatures",
  "bannerEnabled",
  "bannerHeader",
  "bannerText",
] as const satisfies ReadonlyArray<keyof ClientQuery["client"]>;

export type IClientEditableProps = Pick<
  ClientQuery["client"],
  (typeof CLIENT_EDITABLE_PROPS)[number]
> & {
  loginTheme: string;
};

export function ClientEditableFields<T extends IClientEditableProps>({
  form: _form,
  defaultInfo: { default_app_url, keycloakServerInfo },
}: {
  form: UseFormReturn<T, any>;
  defaultInfo: DefaultInfoQuery["defaultInfo"];
}) {
  const form = _form as unknown as UseFormReturn<IClientEditableProps, any>;
  const { control, register } = form;
  const brandColorDatalistId = useId();

  const clientFormInfo = useMemo(
    () => getClientFormInfo({ default_app_url }),
    [default_app_url],
  );

  const { field: clientPermissions } = useController({
    control,
    name: "clientPermissions",
  });

  const { field: clientFeatures } = useController({
    control,
    name: "clientFeatures",
  });

  const entitySingular = form.watch("entityNameSingular");

  return (
    <>
      <TextFieldElement
        data-qa="keycloak-group-name"
        label={"Client name"}
        control={control}
        name={"keycloakGroupName"}
        required
      />

      {clientFormInfo && default_app_url ? (
        <InputBaseURL
          baseUrl={clientFormInfo.baseUrl}
          default_app_url={default_app_url}
          form={form}
        />
      ) : (
        <Skeleton height={90} />
      )}

      <TextFieldElement
        data-qa="users-limit"
        slotProps={{
          htmlInput: { max: GRAPHQL_MAX_INT, min: 0 },
        }}
        control={control}
        label={"Users Limit"}
        name={"usersLimit"}
        type="number"
        required
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
      />
      <SelectElement
        data-qa="login-theme"
        control={control}
        name="loginTheme"
        options={
          keycloakServerInfo.themes?.login?.map(({ name }) => ({
            label: name,
            id: name,
          })) ?? []
        }
        label="Login Theme"
      />
      <TextFieldElement
        data-qa="main-entity-name-singular"
        label={"Entity Name (singular)"}
        control={control}
        name={"entityNameSingular"}
      />
      <TextFieldElement
        data-qa="main-entity-name-plural"
        label={"Entity Name (plural)"}
        control={control}
        name={"entityNamePlural"}
      />
      <TextFieldElement
        data-qa={"insights-placeholder-singular"}
        label={"Insight Placeholder (singular)"}
        control={control}
        name={"insightPlaceholderSingular"}
      />
      <TextFieldElement
        data-qa={"insights-placeholder-plural"}
        label={"Insight Placeholder (plural)"}
        control={control}
        name={"insightPlaceholderPlural"}
      />
      <TextFieldElement
        data-qa="application-title"
        label={"Application Title"}
        control={control}
        name={"applicationTitle"}
      />

      <TextFieldElement
        data-qa="application-logo-url"
        label={"Logo URL"}
        control={control}
        name={"applicationLogoUrl"}
      />
      <SwitchElement
        label="Banner Enabled"
        control={control}
        name="bannerEnabled"
        data-qa={"enable-banner"}
      />
      <TextFieldElement
        data-qa="banner-header"
        label={"Banner Header"}
        control={control}
        name={"bannerHeader"}
      />
      <TextFieldElement
        data-qa="banner-text"
        label={"Banner Text"}
        control={control}
        name={"bannerText"}
      />

      <label
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <input
          data-qa="application-brand-color"
          type="color"
          list={brandColorDatalistId}
          style={{ pointerEvents: "all" }}
          {...register("applicationBrandColor")}
        />
        <datalist id={brandColorDatalistId}>
          <option>#ff0000</option>
          <option>#00ff00</option>
          <option>#0000ff</option>
          <option>#426688</option>
        </datalist>
        Application Brand Color
      </label>
      <SelectElement
        control={control}
        name="proposalNavigationPosition"
        options={[
          {
            id: ProposalNavigationPositionType.Horizontal,
            label: "Horizontal",
          },
          { id: ProposalNavigationPositionType.Vertical, label: "Vertical" },
        ]}
        label={`Set ${entitySingular} Navigation`}
        data-qa="proposal-navigation"
      />
      <SelectPermissions
        clientPermissions={clientPermissions}
        label="Modules available for ADMIN"
        role={Role.Admin}
      />
      <SelectPermissions
        clientPermissions={clientPermissions}
        label="Modules available for FXC_ADMIN"
        role={Role.FxcAdmin}
      />
      <SelectPermissions
        clientPermissions={clientPermissions}
        label="Modules available for USER"
        role={Role.User}
      />
      <SelectPermissions
        clientPermissions={clientPermissions}
        label="Modules available for VIEWER"
        role={Role.Viewer}
      />
      <ClientFeatures clientFeatures={clientFeatures} label="Features" />
    </>
  );
}

function ClientFeatures({
  clientFeatures,
  label,
}: {
  clientFeatures: ControllerRenderProps<IClientEditableProps, "clientFeatures">;
  label: string;
}) {
  const id = useId();
  return (
    <FormControl>
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        sx={{
          maxWidth: 400,
        }}
        labelId={id}
        multiple
        value={clientFeatures.value.map((f) => f.name)}
        onChange={(e) => {
          const selectedValues = e.target.value;
          if (!Array.isArray(selectedValues)) {
            return;
          }
          clientFeatures.onChange(selectedValues.map((f) => ({ name: f })));
        }}
        label={label}
      >
        {Object.values(Feature).map((f, idx) => (
          <MenuItem key={idx} value={f}>
            {f}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SelectPermissions({
  clientPermissions,
  role,
  label,
}: {
  clientPermissions: ControllerRenderProps<
    IClientEditableProps,
    "clientPermissions"
  >;
  role: Role;
  label: string;
}) {
  const id = useId();
  return (
    <FormControl>
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        sx={{
          maxWidth: 400,
        }}
        labelId={id}
        multiple
        value={clientPermissions.value
          .filter((p) => p.role === (role as string))
          .map((p) => p.scope)}
        onChange={(e) => {
          const selectedValues = e.target.value;
          if (!Array.isArray(selectedValues)) {
            return;
          }
          const otherPermissions = clientPermissions.value.filter(
            (p) => p.role !== (role as string),
          );
          clientPermissions.onChange([
            ...otherPermissions,
            ...selectedValues.map((scope) => ({
              role,
              scope,
            })),
          ]);
        }}
        label={label}
      >
        {ScopeOptions.filter((i) => i.availableFor.includes(role)).map(
          (o, idx) => (
            <MenuItem key={idx} value={o.id}>
              {o.label}
            </MenuItem>
          ),
        )}
      </Select>
    </FormControl>
  );
}
